import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowRight, Plus, MessageSquare, Paperclip, Eye, Edit, Trash2, BarChart3, LogOut } from "lucide-react";
import { toast } from "sonner";

const SECTION_LABEL = { quantitative: "كمي", verbal: "لفظي" };

export default function TrainerTopics() {
    const nav = useNavigate();
    const { user, logout } = useAuth();
    const [topics, setTopics] = useState([]);
    const [confirmDel, setConfirmDel] = useState(null);
    const [statsFor, setStatsFor] = useState(null);
    const [stats, setStats] = useState(null);

    const load = async () => {
        try { const { data } = await api.get("/topics"); setTopics(data); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const del = async () => {
        try { await api.delete(`/topics/${confirmDel.id}`); toast.success("تم الحذف"); setConfirmDel(null); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const openStats = async (t) => {
        setStatsFor(t); setStats(null);
        try { const { data } = await api.get(`/topics/${t.id}/read-stats`); setStats(data); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div dir="rtl" className="min-h-screen bg-slate-50">
            <header className="bg-[#0F172A] text-white sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="sm" onClick={() => nav("/trainer")} className="text-white/80 hover:text-white hover:bg-white/10"><ArrowRight className="w-4 h-4 ml-1" /> لوحتي</Button>
                        <BrandLogo size={36} />
                    </div>
                    <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/login"); }} className="text-white/80 hover:text-white hover:bg-white/10"><LogOut className="w-4 h-4 ml-1" /> خروج</Button>
                </div>
            </header>

            <main className="max-w-6xl mx-auto p-6 space-y-5">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-[#0F172A]">موضوعاتي</h1>
                        <p className="text-sm text-slate-500 mt-1">{topics.length} موضوع منشور</p>
                    </div>
                    <Button onClick={() => nav("/trainer/topics/new")} data-testid="btn-new-topic" className="bg-[#1B2A4E] hover:bg-[#0F172A] h-11 px-6"><Plus className="w-4 h-4 ml-1" /> إضافة موضوع جديد</Button>
                </div>

                <div className="grid gap-3">
                    {topics.length === 0 && <div className="text-center py-16 text-slate-400 bg-white rounded-xl border border-slate-200">لم تنشري أي موضوع بعد — ابدئي بموضوعك الأول</div>}
                    {topics.map((t) => (
                        <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-all">
                            <div className="flex items-start gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${t.section === "quantitative" ? "bg-slate-100 text-[#1B2A4E]" : "bg-[#FFF1F2] text-[#C8102E]"}`}>
                                    <BarChart3 className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <h3 className="font-semibold text-[#0F172A] text-lg">{t.title}</h3>
                                        <Badge variant="outline">{SECTION_LABEL[t.section]}</Badge>
                                        {t.has_attachments && <Badge variant="outline" className="text-slate-500"><Paperclip className="w-3 h-3 ml-0.5" /></Badge>}
                                        {t.comments_locked && <Badge variant="outline" className="text-amber-700 border-amber-300">تعليقات مقفلة</Badge>}
                                    </div>
                                    <div className="text-xs text-slate-500 mb-3 font-mono-num" dir="ltr">
                                        {new Date(t.created_at).toLocaleString("ar-EG")} • {t.class_ids?.length || 0} فصل • <MessageSquare className="inline w-3 h-3" /> {t.comments_count || 0}
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button size="sm" variant="outline" onClick={() => nav(`/trainer/topic/${t.id}`)} data-testid={`btn-view-${t.id}`}><Eye className="w-4 h-4 ml-1" /> عرض</Button>
                                        <Button size="sm" variant="outline" onClick={() => openStats(t)} data-testid={`btn-stats-${t.id}`}><BarChart3 className="w-4 h-4 ml-1" /> من فتح؟</Button>
                                        {t.trainer_id === user?.id && (
                                            <>
                                                <Button size="sm" variant="outline" onClick={() => nav(`/trainer/topics/${t.id}/edit`)} data-testid={`btn-edit-${t.id}`}><Edit className="w-4 h-4 ml-1" /> تعديل</Button>
                                                <Button size="sm" variant="outline" onClick={() => setConfirmDel(t)} data-testid={`btn-delete-${t.id}`} className="text-[#C8102E] border-[#FECACA] hover:bg-[#FFF1F2]"><Trash2 className="w-4 h-4 ml-1" /> حذف</Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </main>

            <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
                <AlertDialogContent dir="rtl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>حذف الموضوع</AlertDialogTitle>
                        <AlertDialogDescription>سيتم حذف "{confirmDel?.title}" وجميع تعليقاته وسجلات القراءة. لا يمكن التراجع.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>إلغاء</AlertDialogCancel>
                        <AlertDialogAction onClick={del} className="bg-[#C8102E] hover:bg-[#9F1239]">حذف</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog open={!!statsFor} onOpenChange={(o) => !o && setStatsFor(null)}>
                <DialogContent dir="rtl" className="max-w-2xl max-h-[85vh] flex flex-col">
                    <DialogHeader><DialogTitle>حالة قراءة الموضوع</DialogTitle></DialogHeader>
                    <div className="text-sm text-slate-500 mb-3">{statsFor?.title}</div>
                    {!stats && <div className="text-center py-6 text-slate-400">جاري التحميل...</div>}
                    {stats && (
                        <>
                            <div className="grid grid-cols-3 gap-3 mb-4">
                                <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-2xl font-bold font-mono-num text-[#1B2A4E]">{stats.targeted}</div><div className="text-xs text-slate-500">مستهدفات</div></div>
                                <div className="bg-emerald-50 rounded-lg p-3 text-center"><div className="text-2xl font-bold font-mono-num text-emerald-700">{stats.opened}</div><div className="text-xs text-emerald-700">فتحن</div></div>
                                <div className="bg-[#FFF1F2] rounded-lg p-3 text-center"><div className="text-2xl font-bold font-mono-num text-[#C8102E]">{stats.not_opened}</div><div className="text-xs text-[#C8102E]">لم يفتحن</div></div>
                            </div>
                            <div className="overflow-y-auto space-y-4">
                                <section>
                                    <div className="text-xs font-semibold text-emerald-700 mb-2">✓ فتحن الموضوع</div>
                                    <div className="space-y-1">
                                        {stats.opened_students.length === 0 && <div className="text-xs text-slate-400">—</div>}
                                        {stats.opened_students.map((s) => (
                                            <div key={s.id} className="text-sm border border-slate-100 rounded p-2 bg-emerald-50/40">{s.full_name} <span className="text-xs text-slate-500">— {s.grade} {s.section}</span></div>
                                        ))}
                                    </div>
                                </section>
                                <section>
                                    <div className="text-xs font-semibold text-[#C8102E] mb-2">✗ لم يفتحن الموضوع</div>
                                    <div className="space-y-1">
                                        {stats.not_opened_students.length === 0 && <div className="text-xs text-slate-400">جميع الطالبات فتحن الموضوع 🎉</div>}
                                        {stats.not_opened_students.map((s) => (
                                            <div key={s.id} className="text-sm border border-slate-100 rounded p-2 bg-[#FFF1F2]/50">{s.full_name} <span className="text-xs text-slate-500">— {s.grade} {s.section}</span></div>
                                        ))}
                                    </div>
                                </section>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
